import { useCallback, useEffect, useMemo, useState } from 'react'
import { loginDestination } from './loginReturn'
import { api, noteTokenExpiry, setSessionEndedHandler, signOut as apiSignOut } from './api'
import { SESSION_STATUS, SessionContext, noteSessionEnded } from './sessionContext'
import { session as devSession, buildVersion as devBuildVersion } from '../app/session'
import { brandFromSettings, saveBranding } from '../app/branding'

/**
 * Who is signed in, live from the server.
 *
 * The auth token is an HttpOnly cookie by design, so this app cannot read it and does
 * not try. `GET /api/session` is the whole bootstrap: a 200 is the definition of signed
 * in, a 401 is the definition of anonymous, and everything the shell renders — the
 * display name, the role set, the licensed feature sets, the handful of settings the
 * layout reads — comes back in that one response.
 *
 * It also carries two things a page load used to get for free and a SPA does not:
 *
 *   Gate      the verdict `ForceRedirectionMiddleware` would reach. The middleware still
 *             302s a real request, but once this app is running, navigation never
 *             reaches the server — so a user who must accept the terms would otherwise
 *             keep browsing. `<SessionGate>` re-applies it on every route change.
 *   Warnings  the soft prompts the Razor layout read off `HttpContext.Items`.
 *
 * MAPPING. The wire is PascalCase (`PropertyNamingPolicy = null`) and this is the one
 * place it is translated, into exactly the lowercase shape `src/app/session.js` already
 * had — so `permits()` and `visibleMenu()` in `app/navigation.js` keep working unchanged.
 */

/** SessionDto -> the shape the menu guards and the shell already consume. */
function toSession(dto) {
  return {
    /** The display name the topbar shows — `ViewBag.username` in the legacy layout. */
    username: dto.DisplayName || dto.Username,
    /** The account name, which is what the profile page loads by. */
    login: dto.Username,
    email: dto.Email ?? '',
    company: dto.Company ?? '',
    roles: dto.Roles ?? [],
    /** Empty means the license grants every module — see `permits()`. */
    features: dto.Features ?? [],
    settings: dto.Settings ?? {},
  }
}

export function SessionProvider({ children }) {
  const [state, setState] = useState({
    status: SESSION_STATUS.loading,
    session: null,
    raw: null,
    error: null,
  })

  /**
   * @param {boolean} background  a re-read while the user is already on a screen, rather
   *        than the bootstrap or the retry button. Its ONLY effect is that a thrown error
   *        is swallowed — see the catch.
   */
  const load = useCallback(async (background = false) => {
    try {
      const dto = await api.get('/api/session', { anonymousOk: true })

      if (!dto) {
        noteTokenExpiry(null)
        setState({ status: SESSION_STATUS.anonymous, session: null, raw: null, error: null })
        return
      }

      // api.js refreshes ahead of this, so the token never lapses mid-form.
      noteTokenExpiry(dto.TokenExpiresAt)
      saveBranding(brandFromSettings(dto.Settings))
      setState({ status: SESSION_STATUS.authenticated, session: toSession(dto), raw: dto, error: null })
    } catch (error) {
      /* A BACKGROUND RE-READ MUST NEVER TAKE THE SCREEN AWAY.
       *
       * `SessionGate` renders its `ErrorState` INSTEAD OF `children`, and it wraps
       * `AppLayout` as a route element — so setting `error` here unmounts whatever the user
       * had open: form input, scroll position, table state. On the bootstrap that is right,
       * because there is nothing to lose yet. On a re-read triggered by a navigation or a
       * tab switch it would mean one network blip, one 500, or a laptop waking from sleep
       * throwing away work mid-edit. The last good session simply stands.
       *
       * A 401 is NOT caught here — `anonymousOk` makes it a null `dto` above, which is a
       * real revocation and does route to sign-in. Only a broken answer is swallowed. */
      if (background) return

      // A reachable server that cannot answer is NOT "signed out" — showing the login
      // page for a 500 would make an outage look like an expired session.
      setState({ status: SESSION_STATUS.error, session: null, raw: null, error })
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  /* `reload` is handed straight to a Button's `onClick` in `SessionGate`, which would call
     it with a MouseEvent — truthy, so the retry would silently swallow its own failure and
     stop recovering. Wrapped so the argument cannot leak in. */
  const reload = useCallback(() => load(false), [load])
  const revalidate = useCallback(() => load(true), [load])

  /* When anything ends the session — an expiry, a refused refresh, sign-out — drop it
     here too, so the shell stops rendering a name for someone who is gone. */
  useEffect(() => {
    setSessionEndedHandler(() => {
      setState({ status: SESSION_STATUS.anonymous, session: null, raw: null, error: null })
      if (window.location.pathname !== '/login') {
        /* BEFORE the navigation, because after it this frame is gone. `api.js` throws right
           after calling this, so the screen's own toast never gets shown — the sign-in page
           carries the reason instead. */
        noteSessionEnded()
        window.location.assign(loginDestination())
      }
    })
  }, [])

  const value = useMemo(
    () => ({
      status: state.status,
      /* In development against mocks the API may not be running at all; the static
         session keeps every screen renderable rather than blank. It is a FALLBACK and
         never a source of truth — in production the bootstrap either answers or the
         user is sent to sign in. */
      session: state.session ?? (import.meta.env.DEV ? devSession : null),
      buildVersion: state.raw?.BuildVersion || devBuildVersion,
      gate: state.raw?.Gate ?? null,
      warnings: state.raw?.Warnings ?? { Policies: false, Surveys: false, Assessments: false },
      error: state.error,
      reload,
      /* A re-read that leaves the current session in place if it fails — see `SessionRevalidator`. */
      revalidate,
      signOut: apiSignOut,
    }),
    [state, reload, revalidate],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}
